var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCurrentAccount } from '@mysten/dapp-kit-react';
import { useSignAndExecuteTransaction } from '../hooks/walletCompat';
import { createAlphaFiSDK } from '../utils/networkUtils';
import { AlphaReceiptTransfer, NotificationComp } from 'ui-kit';
import { getIcon } from '../utils/getIcon';
import { formatTimestamp } from '../utils/dateUtils';
import { useBalances } from '../context/BalanceContext';
import { transferReceiptTransactionHandler, } from '../utils/transferReceiptHandler';
const sdk = createAlphaFiSDK('mainnet');
/** Determine the UI transfer stage from the on-chain TransferRequest. */
function resolveStage(transferRequest) {
    if (!transferRequest)
        return 'initiate';
    return Date.now() >= transferRequest.autoAcceptTimestamp ? 'confirm' : 'cooldown';
}
function truncateAddress(addr) {
    if (addr.length <= 12)
        return addr;
    return `${addr.slice(0, 6)}….${addr.slice(-4)}`;
}
const TransferReceipt = ({ themeName }) => {
    const currentAccount = useCurrentAccount();
    const signAndExecuteTransaction = useSignAndExecuteTransaction();
    const { walletBalancesMap } = useBalances();
    const suiBalance = (walletBalancesMap === null || walletBalancesMap === void 0 ? void 0 : walletBalancesMap.get('0x2::sui::SUI')) || '0';
    const suiBalanceRef = useRef(suiBalance);
    suiBalanceRef.current = suiBalance;
    const [receipts, setReceipts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const hasLoadedOnceRef = useRef(false);
    const [notificationInfo, setNotificationInfo] = useState({
        title: '',
        description: '',
    });
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const isTxPending = useRef(false);
    // ── Transaction executor ─────────────────────────────────────────────────────
    const executeTransaction = useCallback((transaction, actionLabel, onSuccess, extraParams) => {
        var _a;
        if (isTxPending.current)
            return;
        isTxPending.current = true;
        transferReceiptTransactionHandler(Object.assign({ transaction,
            actionLabel,
            signAndExecuteTransaction,
            setNotificationInfo,
            setIsNotificationOpen,
            onSuccess, clearUserCaches: (address) => {
                if (address)
                    sdk.clearUserCaches(address);
            }, address: (_a = currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address) !== null && _a !== void 0 ? _a : '', suiBalance: suiBalanceRef.current, onSettled: () => {
                isTxPending.current = false;
            } }, extraParams));
    }, [currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address, signAndExecuteTransaction]);
    // ── Fetch & map receipts ─────────────────────────────────────────────────────
    const fetchReceipts = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!(currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address)) {
            setReceipts([]);
            return;
        }
        // Only show the loader on the very first fetch
        if (!hasLoadedOnceRef.current)
            setIsLoading(true);
        try {
            const alphaFiReceipts = yield sdk.getAlphaFiReceiptsWithTransferRequests(currentAccount.address);
            const address = currentAccount.address;
            const mapped = alphaFiReceipts.map((r) => {
                const transferStage = resolveStage(r.transferRequest);
                const tr = r.transferRequest;
                return {
                    id: r.id,
                    name: 'AlphaFi-Position',
                    address: r.id,
                    addressUrl: `https://suiscan.xyz/mainnet/object/${r.id}`,
                    thumbnail: getIcon('alphaPositionIcon'),
                    transferStage,
                    cooldownRemainingSeconds: tr
                        ? Math.max(0, Math.floor((tr.autoAcceptTimestamp - Date.now()) / 1000))
                        : undefined,
                    initiateIcon: getIcon('sendIcon'),
                    cooldownIcon: getIcon('clockIcon'),
                    darkCooldownIcon: getIcon('darkClockIcon'),
                    confirmIcon: getIcon('whiteTickRounded'),
                    darkConfirmIcon: getIcon('darkTickRounded'),
                    externalLinkIcon: themeName === 'dark' ? getIcon('externalIconWhite') : getIcon('externalIconDark'),
                    themeName,
                    initiateData: {
                        inputPlaceholder: '0x...',
                        walletIcon: getIcon('grayWalletIcon'),
                        senderAddress: address,
                        onInitiate: (receiverAddr) => {
                            executeTransaction(sdk.createTransferRequest({ receiptId: r.id, receiver: receiverAddr }), 'Initiate Transfer', fetchReceipts);
                        },
                        onCancel: () => { },
                    },
                    cooldownData: tr
                        ? {
                            endsAt: formatTimestamp(tr.autoAcceptTimestamp, { relativeDay: true }),
                            recipientAddress: truncateAddress(tr.receiver),
                            copyIcon: themeName === 'dark' ? getIcon('lightCopyIcon') : getIcon('darkCopyIcon'),
                            onCopy: () => navigator.clipboard.writeText(tr.receiver).catch(console.error),
                            onAbort: () => {
                                executeTransaction(sdk.cancelTransferRequest({ receiptId: r.id, address }), 'Abort Transfer', fetchReceipts);
                            },
                        }
                        : undefined,
                    confirmData: tr
                        ? {
                            receiptName: 'AlphaFi-Position',
                            recipientAddress: truncateAddress(tr.receiver),
                            copyIcon: themeName === 'dark' ? getIcon('lightCopyIcon') : getIcon('darkCopyIcon'),
                            onCopy: () => navigator.clipboard.writeText(tr.receiver).catch(console.error),
                            onConfirm: () => {
                                executeTransaction(sdk.fulfillTransferRequest({ receiptId: r.id, address }), 'Confirm Transfer', fetchReceipts, { receiverAddress: tr.receiver });
                            },
                            onAbort: () => {
                                executeTransaction(sdk.cancelTransferRequest({ receiptId: r.id, address }), 'Abort Transfer', fetchReceipts);
                            },
                        }
                        : undefined,
                };
            });
            setReceipts(mapped);
            hasLoadedOnceRef.current = true;
        }
        catch (err) {
            console.error('Failed to fetch AlphaFi receipts:', err);
            setReceipts([]);
        }
        finally {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address, themeName]);
    useEffect(() => {
        if (currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address)
            sdk.clearUserCaches(currentAccount.address);
        hasLoadedOnceRef.current = false;
    }, [currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address]);
    useEffect(() => {
        fetchReceipts();
    }, [fetchReceipts]);
    // Automatically re-fetch when the cooldown period expires so the timeline moves to the confirm stage
    useEffect(() => {
        const cooldownReceipts = receipts.filter((r) => r.transferStage === 'cooldown' &&
            r.cooldownRemainingSeconds != null &&
            r.cooldownRemainingSeconds > 0);
        if (cooldownReceipts.length === 0)
            return;
        const minRemainingMs = Math.min(...cooldownReceipts.map((r) => r.cooldownRemainingSeconds)) * 1000;
        const timerId = setTimeout(() => fetchReceipts(), minRemainingMs + 1000);
        return () => clearTimeout(timerId);
    }, [receipts, fetchReceipts]);
    return (_jsxs("div", { className: "theme-bg-alphafi-main-body min-h-screen mx-auto px-[20px] lg:px-[72px] xl:px-[unset] mt-[-1px]", children: [_jsx("div", { className: "w-full xl:max-w-[1390px] mx-auto min-h-screen py-10", children: isLoading ? (_jsx("div", { className: "flex items-center justify-center py-20", children: _jsx("span", { className: "text-lg font-InterRegular theme-text-alphafi-secondary", children: "Loading receipts..." }) })) : !(currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address) ? (_jsx("div", { className: "flex items-center justify-center py-20", children: _jsx("span", { className: "text-lg font-InterRegular theme-text-alphafi-secondary", children: "Connect your wallet to view receipts." }) })) : receipts.length === 0 ? (_jsx("div", { className: "flex items-center justify-center py-20", children: _jsx("span", { className: "text-lg font-InterRegular theme-text-alphafi-secondary", children: "No receipts found for this wallet." }) })) : (_jsx(AlphaReceiptTransfer, { receipts: receipts })) }), _jsx(NotificationComp, { isOpen: isNotificationOpen, setIsOpen: setIsNotificationOpen, info: notificationInfo })] }));
};
export default TransferReceipt;
